/** What one account has put into the shared map, worked out from its uploads.
 *
 * The service keeps uploads, not contributions: a row per document received,
 * with the circuit it was filed against and what became of it. The profile
 * wants the other axis — one row per circuit, with the metres that landed and
 * where the latest survey stands — so it is derived here rather than asked
 * for, and derived the same way `/api/me` counts `metres_added`: edges on
 * merged uploads only. What has not been merged is not on the map, however
 * much of it there is.
 *
 * Two decisions are worth naming. The status shown is the *latest* upload's,
 * not the best one's, because the question a contributor is asking is "where
 * is the survey I just sent", and a circuit that merged last week and is
 * pending again today should say pending. And a merged upload that added no
 * metres is called `corroborated` rather than `merged`: it landed, it counted
 * towards somebody else's quorum, and it drew nothing new — which is a real
 * contribution and reads as a mistake if it is shown as zero metres merged.
 *
 * Pure, and exported on its own, so the rule can be tested without a screen.
 */

import type { MyUpload, UploadStatus } from '@/lib/types';

export type ContributionStatus = UploadStatus | 'corroborated';

export interface TrackContribution {
  official_id: string;
  track_name: string;
  /** Metres of border this account's merged uploads carried. */
  metres: number;
  status: ContributionStatus;
  /** The server's reason for the latest status, when it gave one. */
  reason: string;
  /** The issue that explains a held upload, when there is one. */
  issue_type: string | null;
}

interface Bucket {
  official_id: string;
  track_name: string;
  metres: number;
  latest: MyUpload;
}

/** One row per circuit, the largest contribution first. */
export function contributionsFrom(uploads: readonly MyUpload[]): TrackContribution[] {
  const byTrack = new Map<string, Bucket>();

  for (const upload of uploads) {
    const held = byTrack.get(upload.official_id);
    const bucket: Bucket = held ?? {
      official_id: upload.official_id,
      track_name: upload.track_name,
      metres: 0,
      latest: upload,
    };
    if (upload.status === 'merged') bucket.metres += upload.edges;
    // The listing arrives newest first, but nothing in the reply promises
    // that, so the comparison is made rather than assumed.
    if (upload.received_at > bucket.latest.received_at) {
      bucket.latest = upload;
      bucket.track_name = upload.track_name;
    }
    byTrack.set(upload.official_id, bucket);
  }

  const rows: TrackContribution[] = [];
  for (const bucket of byTrack.values()) {
    const latest = bucket.latest;
    rows.push({
      official_id: bucket.official_id,
      track_name: bucket.track_name || bucket.official_id,
      metres: bucket.metres,
      status:
        latest.status === 'merged' && bucket.metres === 0 ? 'corroborated' : latest.status,
      reason: latest.status_reason,
      issue_type: latest.issue?.type ? String(latest.issue.type) : null,
    });
  }

  return rows.sort(
    (a, b) => b.metres - a.metres || a.track_name.localeCompare(b.track_name),
  );
}

/** The colour a status wears in the contribution list. Held is warm, rejected
 *  is the error hue, and a corroboration is deliberately quiet. */
export function contributionColour(status: ContributionStatus): string {
  switch (status) {
    case 'merged':
      return 'var(--color-accent-300)';
    case 'pending':
      return 'var(--color-neutral-400)';
    case 'held':
      return 'var(--color-warn)';
    case 'rejected':
      return 'var(--color-error)';
    case 'corroborated':
      return 'var(--color-neutral-600)';
  }
}

/** `held: gate_failed`, `corroborated only`, or the status on its own. */
export function contributionLabel(row: TrackContribution): string {
  if (row.status === 'held') return row.issue_type ? `held: ${row.issue_type}` : 'held';
  if (row.status === 'corroborated') return 'corroborated only';
  return row.status;
}
